// Stats section renderer for the endgame modal.
//
// Pure-DOM, no game/state imports beyond the LifetimeStats type. The renderer
// is idempotent — calling it twice with the same stats produces identical
// DOM, so the orchestrator can re-render on every modal open without
// tracking which stats snapshot is currently on screen.

import type { LifetimeStats } from '../game/stats'
import { computeWinPercentage } from '../game/stats'

export interface StatsSectionElements {
  readonly summaryRow: HTMLElement
  readonly streakRow: HTMLElement
  readonly sundayRow: HTMLElement
}

export function renderStatsSection(
  els: StatsSectionElements,
  stats: LifetimeStats,
): void {
  // Summary row: omit Win % entirely when no games are played so the very
  // first session's modal doesn't show "Win %: 0".
  const winPct = computeWinPercentage(stats)
  els.summaryRow.textContent =
    winPct === null
      ? `Played: ${String(stats.played)}`
      : `Played: ${String(stats.played)} · Win %: ${String(winPct)}`

  // Streak row is always present once any game has been played; before that
  // the surrounding modal isn't shown (no terminal state has been reached),
  // so we don't gate this row.
  els.streakRow.textContent = `Streak: ${String(stats.currentStreak)} · Max streak: ${String(stats.maxStreak)}`

  // Sunday row: hidden until the player has either an active or all-time
  // Sunday streak. Avoids surfacing Sunday-streak terminology to players who
  // have never played on a Sunday.
  const showSunday =
    stats.played > 0 && (stats.sundayStreak > 0 || stats.maxSundayStreak > 0)
  els.sundayRow.hidden = !showSunday
  if (showSunday) {
    els.sundayRow.textContent = `Sunday streak: ${String(stats.sundayStreak)} · Max Sunday: ${String(stats.maxSundayStreak)}`
  } else {
    // Clear stale text so a player whose Sunday streak was reset to 0 (e.g.
    // missed Sunday) doesn't see ghost text behind the hidden attribute.
    els.sundayRow.textContent = ''
  }
}
